import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RegExp } from 'src/app/core/config/regex';
import { Project, Team } from 'src/app/core/models/project-pilot.model';
import { Context } from 'src/app/shared/models/context';
import { Message } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { ProjectPilotService } from 'src/app/shared/services/project-pilot.service';
import { SeparationService } from 'src/app/shared/services/separation.service';
import { Editor, Toolbar } from 'ngx-editor';

@Component({
  selector: 'app-manage-project',
  templateUrl: './manage-project.component.html',
  styleUrls: ['./manage-project.component.scss']
})
export class ManageProjectComponent implements OnInit {

  @Input() project: Project;
  @Input() context: Context = {} as Context;
  @Input() projects: Project[] = [];
  @Output() emitter = new EventEmitter<Message>();
  public projectForm: FormGroup;
  public searchKeyword: string = "";
  public selectedMemberIds: number[] = [];
  public submitted: boolean = false;
  private modified: boolean = false;
  public statuses: string;
  public priorities: string;
  public riskLevels: string;
  public types: string;
  public completionQualities: string;
  public teams: Team[] = [];
  public projectId: number;
  public editor: Editor;
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
    private projectPilotService: ProjectPilotService,
    private dataService: DataService,
    private datePipe: DatePipe,
    private separationService: SeparationService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.editor = new Editor();
    this.setProject(this.project);
    this.route.queryParams.subscribe(params => {
      this.projectId = params['pId'];
      if (this.projectId) {
        this.projectPilotService.getProject(this.projectId).subscribe(project => {
          if (this.projectForm) {
            if (project.startDate) {
              project.startDate = this.datePipe.transform(project.startDate, 'yyyy-MM-dd');
            }
            this.projectForm.patchValue(project);
          }
        });
      }
    });
    this.projectPilotService.getStatuses().subscribe(statuses => (this.statuses = statuses));
    this.projectPilotService.getPriorities().subscribe(priorities => (this.priorities = priorities));
    this.projectPilotService.getRiskLevels().subscribe(riskLevels => (this.riskLevels = riskLevels));
    this.projectPilotService.getProjectTypes().subscribe(types => (this.types = types));
    this.projectPilotService.getCompletionQuality().subscribe(completionQualities => (this.completionQualities = completionQualities));
    this.projectPilotService.getTeams(false).subscribe(teams => (this.teams = teams));
  }

  private setProject(project?: Project) {
    const today = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    if (!project)
      project = {
        id: null,
        code: "",
        name: "",
        notes: "",
        description: "",
        startDate: "",
        hoursSpent: null,
        teamSize: null,
        progressPercentage: null,
        milestoneCount: null,
        archived: false,
        disabled: false,
        status: 'NOT_STARTED',
        priority: 'LOW',
        riskLevel: 'LOW',
        completionQuality: 'SATISFACTORY',
        type: 'PRODUCT',
        companyId: null,
        teamId: null,
        companyName: "",
        teamName: "",
      } as Project;
    this.projectForm = this.fb.group({
      id: this.fb.control(project?.id || null),
      code: this.fb.control(project.code?.toUpperCase() || '', [Validators.required, Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d-]+$/)]),
      name: this.fb.control(project?.name, [Validators.required]),
      notes: this.fb.control(project?.notes, []),
      description: this.fb.control(project?.description, []),
      startDate: this.fb.control(
        project.startDate ? this.datePipe.transform(project.startDate, 'yyyy-MM-dd') : today,
        [Validators.required]
      ),
      hoursSpent: this.fb.control(project?.hoursSpent, []),
      teamSize: this.fb.control(project?.teamSize, []),
      progressPercentage: this.fb.control(project?.progressPercentage, []),
      milestoneCount: this.fb.control(project?.milestoneCount, []),
      archived: this.fb.control(project?.archived, []),
      disabled: this.fb.control(project.disabled, []),
      status: this.fb.control(project?.status, []),
      priority: this.fb.control(project?.priority, []),
      riskLevel: this.fb.control(project?.riskLevel, []),
      completionQuality: this.fb.control(project?.completionQuality, []),
      type: this.fb.control(project?.type, []),
      companyId: this.fb.control(project.companyId, []),
      teamId: this.fb.control(project.teamId, [Validators.required]),
      companyName: this.fb.control(project.companyName, []),
      teamName: this.fb.control(project.teamName, []),
    });
    this.projectForm.valueChanges.subscribe(value => this.modified = true);
  }

  get p() {
    return this.projectForm.controls;
  }

  public saveProject(inProgress?: any) {
    this.submitted = true;
    if (this.projectForm.valid) {
      const project: Project = this.projectForm.value;
      const nameExists = this.projects.some(
        h => h.name.toLowerCase() === project.name.toLowerCase()
      );
      const codeExists = this.projects.some(
        h => h.code.toLowerCase() === project.code.toLowerCase()
      );
      if (nameExists || codeExists) {
        if (nameExists && codeExists) {
          this.commonService.setToastr(400, `Project with name '${project.name}' and code '${project.code}' already exists!`);
        } else if (nameExists) {
          this.commonService.setToastr(400, `Project name '${project.name}' already exists!`);
        } else {
          this.commonService.setToastr(400, `Project code '${project.code}' already exists!`);
        }
        return;
      }
      if (!this.modified) {
        this.commonService.setToastr(200, `Project ${project.code} has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (project.id) {
        this.projectPilotService.updateProject(project.id, project).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.commonService.setToastr(200, response.text);
            } else {
              this.commonService.setToastr(500, response.text || 'Unable to complete the action. Please try again.');
            }
            inProgress.value = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error.message || 'Server error occurred while updating the project.');
          },
        });
      } else {
        this.projectPilotService.createProject(project).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              const projectId = parseInt(response.payload);
              this.p.id.setValue(projectId);
              this.projectPilotService.getProjects().subscribe((projects: Project[]) => {
                this.dataService.setProjects(projects); 
              });
              this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { pId: projectId },
                queryParamsHandling: 'merge',
              });

              this.commonService.setToastr(200, response.text);
            } else {
              this.commonService.setToastr(500, response.text || 'Unable to complete the action. Please try again.');
            }
            inProgress.value = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error.message || 'Server error occurred while creating the project.');
          },
        });
      }
    }
  }

  public getBadgeClassForType(type: string): string {
    switch (type?.toUpperCase()) {
      case 'PRODUCT':
        return 'badge bg-primary text-white rounded-pill';
      case 'TECHNICAL PARTNERSHIP':
        return 'badge bg-success text-white rounded-pill';
      case 'SERVICE':
        return 'badge bg-warning text-dark rounded-pill';
      case 'RESEARCH':
        return 'badge bg-info text-dark rounded-pill';
      case 'INTERNAL':
        return 'badge bg-dark text-white rounded-pill';
      default:
        return 'badge bg-secondary text-white rounded-pill';
    }
  }

  public formatType(type: string): string {
    return type.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  public getBadgeClassForQuality(quality: string): string {
    switch (quality?.toUpperCase()) {
      case 'EXCELLENT':
        return 'badge bg-success text-white rounded-pill';
      case 'GOOD':
        return 'badge bg-primary text-white rounded-pill';
      case 'SATISFACTORY':
        return 'badge bg-warning text-dark rounded-pill';
      case 'POOR':
        return 'badge bg-danger text-white rounded-pill';
      default:
        return 'badge bg-secondary text-white rounded-pill';
    }
  }

  public formatQuality(label: string): string {
    return label?.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }


  public getBadgeClassForRisk(risk: string): string {
    switch (risk?.toUpperCase()) {
      case 'LOW':
        return 'badge bg-secondary text-white rounded-pill';
      case 'MEDIUM':
        return 'badge bg-warning text-dark rounded-pill';
      case 'HIGH':
        return 'badge bg-danger text-white rounded-pill';
      default:
        return 'badge bg-success text-white rounded-pill';
    }
  }

  public formatRisk(label: string): string {
    return label
      ?.toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  public getBadgeClassForStatus(status: string): string {
    switch (status?.toUpperCase()) {
      case 'NOT_STARTED':
        return 'badge bg-secondary text-white rounded-pill';
      case 'IN_PROGRESS':
        return 'badge bg-primary text-white rounded-pill';
      case 'COMPLETED':
        return 'badge bg-success text-white rounded-pill';
      case 'ON_HOLD':
        return 'badge bg-warning text-dark rounded-pill';
      case 'CANCELLED':
        return 'badge bg-danger text-white rounded-pill';
      default:
        return 'badge bg-light text-dark rounded-pill';
    }
  }

  public formatStatus(label: string): string {
    return label
      ?.toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  public getBadgeClassForPriority(priority: string): string {
    switch (priority?.toUpperCase()) {
      case 'LOW':
        return 'badge bg-secondary text-white rounded-pill';
      case 'MEDIUM':
        return 'badge bg-warning text-dark rounded-pill';
      case 'HIGH':
        return 'badge bg-primary text-white rounded-pill';
      case 'CRITICAL':
        return 'badge bg-danger text-white rounded-pill';
      default:
        return 'badge bg-success text-white rounded-pill';
    }
  }

  public formatPriority(label: string): string {
    return label
      ?.toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }
}

